import axios, { AxiosResponse } from 'axios'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { AdminPaymentResponseType, AdminPaymentType } from 'Types/admin/admin'
import { CancelFacturePayloadProps } from 'Types/global.types.ts'
import { FactureType } from 'Definitions/Facture.type.ts'

import { ADMIN_API_URL } from 'Constants/Global.admin.constants.ts'

import { getAxiosBlobConfig, getAxiosConfig } from 'Utils/auth.util.ts'
import { handleError } from 'Helpers/errors.helper.ts'

/**
 * Get invoices by month
 */
export const downloadInvoice = createAsyncThunk(
  'admin/downloadInvoice',
  async ({ month = '', year = '1' }: { month: string; year?: string }) => {
    let url = ADMIN_API_URL.FACTURE.MONTHLY_DOWNLOAD
    const queryParams = []

    if (month) {
      queryParams.push(`month=${encodeURIComponent(month)}`)
    }

    if (year) {
      queryParams.push(`year=${encodeURIComponent(year)}`)
    }

    if (queryParams.length > 0) {
      const queryString = queryParams.join('&')
      url = `${url}?${queryString}`
    }

    try {
      const data: AxiosResponse = await axios.get(url, getAxiosBlobConfig())
      return data
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

type InvoicesRequestProps = {
  userId?: string
  param?: string
  sort?: 'CREATED_AT' | 'PAID'
  order?: 'ASC' | 'DESC'
  minDate?: string
  maxDate?: string
  pageIndex: number
  pageSize: number
}

export const getInvoicesByMonthAsync = async ({
  userId,
  param,
  sort = 'CREATED_AT',
  order = 'DESC',
  minDate,
  maxDate,
  pageIndex,
  pageSize,
}: InvoicesRequestProps) => {
  let url = ADMIN_API_URL.FACTURE.SEARCH
  const queryParams = []

  if (userId) {
    queryParams.push(`userId=${encodeURIComponent(userId)}`)
  }

  if (param || param === '') {
    queryParams.push(`param=${encodeURIComponent(param)}`)
  }

  if (sort) {
    queryParams.push(`sort=${encodeURIComponent(sort)}`)
  }

  if (order) {
    queryParams.push(`order=${encodeURIComponent(order)}`)
  }

  if (minDate) {
    queryParams.push(`minDate=${encodeURIComponent(minDate)}`)
  }

  if (maxDate) {
    queryParams.push(`maxDate=${encodeURIComponent(maxDate)}`)
  }

  if (queryParams.length > 0) {
    const queryString = queryParams.join('&')
    url = `${url}?${queryString}&page=${pageIndex}&limit=${pageSize}`
  }

  try {
    const { data }: AxiosResponse<AdminPaymentResponseType> = await axios.get(url, getAxiosConfig())
    return {
      invoices: data.data,
      total: data.total,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
}

export const getInvoicesByMonth = createAsyncThunk('admin/getInvoicesByMonth', getInvoicesByMonthAsync)

export const updateInvoicePaymentStatus = createAsyncThunk('admin/updateInvoicePaymentStatus', async (id: string) => {
  const url = `${ADMIN_API_URL.FACTURE.ROOT}/${id}/paid`

  try {
    const { data }: AxiosResponse<{ data: AdminPaymentType }> = await axios.patch(url, null, getAxiosConfig())
    return {
      invoice: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
})

type ProductProps = {
  reference: string
  title: string
  unitPrice: number
  quantity: number
}

interface CustomFactureProps {
  customer: {
    userId?: string
    externalUserId?: string
    paymentMethod: string
    shippingCost: number
    delivery: string
  }
  products: ProductProps[]
}

/**
 * Create a custom facture
 */
export const createCustomFacture = createAsyncThunk('admin/addCustomFacture', async (options: CustomFactureProps) => {
  const url = `${ADMIN_API_URL.FACTURE.ROOT}/custom`

  try {
    const { data }: AxiosResponse<{ data: AdminPaymentType }> = await axios.post(url, options, getAxiosConfig())
    return {
      invoice: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
})

export const toggleFactureText = createAsyncThunk(
  'admin/toggleFactureText',
  async ({
    factureId,
    checkboxText,
    type,
  }: {
    factureId: string
    checkboxText: string
    type: string // 'ORDER' | 'PURCHASE'
  }) => {
    const url = `${ADMIN_API_URL.FACTURE.ROOT}/${factureId}/checkboxText`

    try {
      const { data }: AxiosResponse<{ data: FactureType }> = await axios.patch(url, { checkboxText }, getAxiosConfig())
      return {
        facture: data.data,
        type,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

export const cancelFacture = createAsyncThunk(
  'admin/cancelFacture',
  async ({ factureId, entityType }: CancelFacturePayloadProps) => {
    const url = `${ADMIN_API_URL.FACTURE.ROOT}/${factureId}/cancel`

    try {
      const { data }: AxiosResponse<{ data: FactureType }> = await axios.patch(url, null, getAxiosConfig())
      return {
        facture: data.data,
        entityType,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

export const getMonthlyFacture = async ({ month, year }: { month: string; year: string }) => {
  const url = `${ADMIN_API_URL.FACTURE.ROOT}/monthly-facture-download?month=${month}&year=${year}`

  try {
    const data: AxiosResponse = await axios.get(url, getAxiosBlobConfig())
    return data
  } catch (error) {
    handleError(error, true)
    throw error
  }
}

export const regenerateMonthlyFacture = async (id: string) => {
  const url = `${ADMIN_API_URL.FACTURE.ROOT}/${id}/regenerate-monthly`

  try {
    const { data }: AxiosResponse<{ data: AdminPaymentType }> = await axios.put(url, null, getAxiosConfig())
    return {
      facture: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
}

export const removeCustomInvoice = async (factureId: string) => {
  const url = `${ADMIN_API_URL.FACTURE.ROOT}/${factureId}/custom`

  try {
    const { data }: AxiosResponse<{ data: FactureType }> = await axios.delete(url, getAxiosConfig())
    return {
      facture: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
}
